import { useEffect, useRef, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { CloseIcon, SearchIcon } from '@/components/icons';
import { cn } from '@/lib/utils/cn';

interface SearchInputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'onChange' | 'value'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Délai avant remontée de la saisie, en millisecondes. */
  debounceMs?: number;
  /** Action intégrée au bord droit du champ. */
  trailingAction?: ReactNode;
}

/**
 * Champ de recherche à saisie différée.
 *
 * L'état local suit la frappe pour que le champ reste réactif, mais la valeur
 * n'est remontée au parent qu'après une pause. Sans cela, « ingénierie »
 * déclencherait onze requêtes serveur — dix inutiles, et dont les réponses
 * peuvent revenir dans le désordre et réafficher un résultat périmé.
 */
export const SearchInput = ({
  value,
  onValueChange,
  debounceMs = 300,
  trailingAction,
  className,
  ...rest
}: SearchInputProps) => {
  const [draft, setDraft] = useState(value);
  const [lastValue, setLastValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Resynchronisation quand le parent réinitialise la recherche (bouton
   * « effacer », changement d'onglet).
   *
   * Ajustement pendant le rendu, et non dans un `useEffect` : c'est le motif
   * recommandé par React pour dériver un état d'une prop. Passer par un effet
   * provoquerait un premier rendu avec l'ancienne valeur, immédiatement suivi
   * d'un second — un rendu en cascade que React sait ici éviter en relançant
   * le composant avant de valider l'écran.
   */
  if (value !== lastValue) {
    setLastValue(value);
    setDraft(value);
  }

  useEffect(() => {
    if (draft === value || debounceMs <= 0) return;

    const timer = setTimeout(() => {
      onValueChange(draft);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [draft, value, debounceMs, onValueChange]);

  return (
    <div
      className={cn(
        'group border-navy-200/80 relative flex items-center rounded-[10px] border bg-navy-50/60 bg-linear-to-b from-white/80 to-transparent',
        'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),inset_0_-1px_0_0_rgb(15_42_95/0.03),0_2px_4px_-1px_rgb(15_42_95/0.08)]',
        !rest.disabled && 'hover:border-navy-300',
        rest.disabled && 'opacity-60',
        'focus-within:border-accent-700 focus-within:ring-accent-700/12 focus-within:ring-[3px]',
        'transition-[border-color,box-shadow] duration-200 motion-reduce:transition-none',
        className,
      )}
    >
      <span
        aria-hidden
        className="bg-navy-50 text-navy-600 group-focus-within:bg-accent-50 group-focus-within:text-accent-700 pointer-events-none ml-2 grid size-8 shrink-0 place-items-center rounded-md text-[17px] transition-colors"
      >
        <SearchIcon />
      </span>
      <input
        ref={inputRef}
        type="search"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          if (debounceMs <= 0) onValueChange(event.target.value);
        }}
        className={cn(
          'text-navy-900 placeholder:text-ink-500 h-11 min-w-0 w-full rounded-[10px] bg-transparent px-3 text-sm outline-none disabled:cursor-not-allowed',
          'caret-accent-700 selection:bg-accent-100 selection:text-accent-900',
          // Masque la croix native de Chrome, qui double le bouton d'effacement
          // et ne suit pas la charte.
          '[&::-webkit-search-cancel-button]:appearance-none',
        )}
        {...rest}
      />
      {draft && !rest.disabled && !rest.readOnly ? (
        <button
          type="button"
          aria-label="Effacer la recherche"
          onClick={() => {
            setDraft('');
            onValueChange('');
            inputRef.current?.focus();
          }}
          className="text-ink-500 hover:bg-navy-100/70 hover:text-navy-900 focus-visible:ring-accent-700/25 mr-2 grid size-8 shrink-0 cursor-pointer place-items-center rounded-md focus-visible:ring-2 focus-visible:outline-none"
        >
          <CloseIcon />
        </button>
      ) : null}
      {trailingAction ? <span className="flex shrink-0 self-stretch">{trailingAction}</span> : null}
    </div>
  );
};
